import { Controller } from "@hotwired/stimulus"

// reference-month-suggestion — mantém a competência sugerida em sincronia com o
// cartão E a data da compra no form de nova compra.
//
// Por que existe: a competência de uma compra de cartão depende do fechamento
// ("comprou depois do dia X ⇒ cai na fatura do mês seguinte"). O #new pré-preenche
// com um CHUTE (o primeiro cartão ativo), então quem tem mais de um cartão via o
// form sugerir a competência do cartão errado, calado. Aqui a sugestão passa a
// seguir o cartão de fato escolhido.
//
// A regra NÃO é replicada em JS: quem responde é o ReferenceMonthResolver (Ruby,
// testado) via GET purchases/suggest_month.
//
// Regra de ouro: só sugere enquanto o usuário não editou a competência à mão.
// Um toque no campo desliga a sugestão para sempre (nesta visita) — sobrescrever
// uma escolha explícita seria pior que o bug que isto corrige.
//
// Uso: data-controller="reference-month-suggestion" no form, com:
//   targets: card (<select>), date (input da compra), month (input da competência),
//            hint (opcional, <p> onde o "por quê" aparece)
//   values:  url (endpoint)
export default class extends Controller {
  static targets = ["card", "date", "month", "hint"]
  static values = { url: String }

  connect() {
    // Se o form já vem com competência preenchida na EDIÇÃO de uma compra, aquilo é
    // dado salvo — não é sugestão nossa para sobrescrever.
    this.dirty = this.element.dataset.referenceMonthSuggestionEditing === "true"
  }

  // Chamado pelo input da competência: o usuário assumiu o controle.
  markDirty() {
    this.dirty = true
    this._clearHint()
  }

  // Chamado ao trocar cartão ou data.
  async suggest() {
    if (this.dirty || !this.hasMonthTarget || !this.urlValue) return

    const params = new URLSearchParams()
    if (this.hasCardTarget) params.set("card_id", this.cardTarget.value || "")
    if (this.hasDateTarget) params.set("on", this.dateTarget.value || "")

    let data
    try {
      const res = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "application/json" }
      })
      if (!res.ok) return
      data = await res.json()
    } catch {
      return // offline/erro: mantém o que está no campo, sem quebrar o form
    }

    if (this.dirty) return // o usuário pode ter digitado enquanto o fetch voava
    this._setMonth(data.month)
    this._setHint(data.hint)
  }

  // O campo da competência é um <select> gerenciado pelo Tom Select. Setar `.value`
  // sozinho não atualiza o que o Tom Select mostra, então usamos a API dele quando
  // presente (`setValue(v, silent)` — silent = não dispara `change`, para não acionar
  // o markDirty desta própria sugestão). Fallback para <select> nativo (no-JS).
  _setMonth(value) {
    if (!value || this.monthTarget.value === value) return
    const ts = this.monthTarget.tomselect
    if (ts) {
      // O range de month_options (±72 meses) cobre qualquer sugestão realista, então
      // a opção já existe. setValue(v, true): true = silent (não dispara markDirty).
      ts.setValue(value, true)
    } else {
      this.monthTarget.value = value
    }
  }

  _setHint(text) {
    if (!this.hasHintTarget) return
    this.hintTarget.textContent = text || ""
    this.hintTarget.classList.toggle("hidden", !text)
  }

  _clearHint() {
    this._setHint(null)
  }
}
